import { Link } from 'react-router'
import { BadgeCheck, CalendarDays, Gamepad2, Globe2, Star, Trophy } from 'lucide-react'
import { BannerArt } from '../brand/BannerArt.jsx'
import { Card, Pill, StatusDot } from '../ui/primitives.jsx'
import { accentStyle, cn, formatPrize, platformsLabel } from '../../lib/format.js'
import { formatDateRange, relativeTime } from '../../lib/time.js'

export function eventStatus(event, now = new Date()) {
  if (event.status === 'live') return { status: 'live', label: 'Live now' }
  if (event.status === 'completed') return { status: 'completed', label: 'Completed' }
  if (event.status === 'cancelled') return { status: 'cancelled', label: 'Cancelled' }
  if (event.registration?.open) return { status: 'open', label: 'Registration open' }
  return { status: 'upcoming', label: `Starts ${relativeTime(event.startsAt, now)}` }
}

export function OrgLine({ org, className }) {
  return (
    <p className={cn('flex min-w-0 items-center gap-1.5 text-xs font-medium text-ink-muted', className)}>
      <Link to={`/o/${org.slug}`} className="relative z-10 truncate rounded hover:text-ink">
        {org.name}
      </Link>
      {org.verified && (
        <>
          <BadgeCheck aria-hidden="true" className="size-3.5 shrink-0 text-secondary-400" />
          <span className="sr-only">(verified organizer)</span>
        </>
      )}
    </p>
  )
}

function Meta({ icon: Icon, children, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon aria-hidden="true" className="size-3.5 text-ink-muted" />
      <span className="sr-only">{label}: </span>
      {children}
    </span>
  )
}

export function FeaturedEventCard({ event, now }) {
  const status = eventStatus(event, now)
  const prize = formatPrize(event.prizePool)
  return (
    <Card as="article" interactive className="group relative flex flex-col overflow-hidden" style={accentStyle(event.accent)}>
      <div className="relative h-36 overflow-hidden">
        {event.bannerUrl ? (
          <img src={event.bannerUrl} alt="" loading="lazy" className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
        ) : (
          <BannerArt
            seed={event.slug}
            accent={event.accent}
            className="absolute inset-0 size-full transition-transform duration-500 group-hover:scale-[1.03]"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/30 to-transparent" />
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          <span className="rounded-full bg-page/80 px-2.5 py-1 ring-1 ring-white/10 backdrop-blur">
            <StatusDot status={status.status} label={status.label} />
          </span>
          {event.featuredRank && event.visibility !== 'draft' && (
            <Pill tone="primary" icon={Star} className="bg-page/80 backdrop-blur">
              Featured
            </Pill>
          )}
          {event.visibility && event.visibility !== 'public' && (
            <Pill className="bg-page/80 capitalize backdrop-blur">{event.visibility}</Pill>
          )}
          {event.hidden && (
            <Pill tone="danger" className="bg-page/80 backdrop-blur">
              Taken down
            </Pill>
          )}
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5 pt-1">
        <OrgLine org={event.org} />
        <h3 className="font-display text-xl leading-tight font-semibold tracking-wide uppercase">
          <Link to={`/e/${event.slug}`} className="rounded after:absolute after:inset-0 after:rounded-2xl">
            {event.name}
          </Link>
        </h3>
        {event.tagline && <p className="line-clamp-2 text-sm text-ink-muted">{event.tagline}</p>}
        <div className="mt-auto flex flex-wrap gap-x-4 gap-y-2 pt-2 text-xs text-ink">
          <Meta icon={CalendarDays} label="Dates">
            {formatDateRange(event.startsAt, event.endsAt)}
          </Meta>
          <Meta icon={Globe2} label="Regions">
            {event.regions.join(', ') || 'Global'}
          </Meta>
          <Meta icon={Gamepad2} label="Platforms">
            {platformsLabel(event.platforms)}
          </Meta>
          {prize && (
            <Meta icon={Trophy} label="Prize pool">
              <span className="font-semibold text-primary-300">{prize}</span>
            </Meta>
          )}
        </div>
      </div>
      <div aria-hidden="true" className="tint-bar h-1 w-full opacity-80" />
    </Card>
  )
}
